import React, { useEffect, useRef, useState } from "react";
import { auth, db } from "../../db/firebase";
import { getDoc, doc, updateDoc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { useTranslation } from "react-i18next";
import {
  HiOutlineCamera,
  HiOutlineEnvelope,
  HiOutlineBuildingOffice2,
  HiOutlineShieldCheck,
  HiOutlineUser,
  HiOutlineCheckCircle,
  HiOutlineHome,
} from "react-icons/hi2";
import { LoadingSpinner } from "../../components/Loading";
import {
  CloudinaryUploadError,
  uploadProfileImage,
} from "../../utils/cloudinaryUpload";
import { useAuth } from "../../context/AuthContext";
import {
  getPreviewMemberHome,
  setPreviewMemberHome,
} from "../../utils/dashboardViewPref";

const glassBtn =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors disabled:opacity-60";

const inputBase =
  "w-full px-4 py-3 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/80 dark:bg-gray-900/40 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-colors";

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { refresh, isAdmin } = useAuth();
  const [user] = useAuthState(auth);
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState("");
  const [previewMemberHome, setPreviewMemberHomeState] = useState(
    getPreviewMemberHome,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePreviewMemberHomeToggle = (enabled: boolean) => {
    setPreviewMemberHomeState(enabled);
    setPreviewMemberHome(enabled);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          setFirstName(data.firstName || "");
          setLastName(data.lastName || "");
        } else {
          setError(t("userDataNotFound"));
        }
      } catch (err) {
        setError(t("failedToFetchUserData"));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user, t]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;

    setUploading(true);
    setUploadProgress(0);
    setSaveMessage("");
    setSaveError("");

    try {
      const downloadURL = await uploadProfileImage(file, setUploadProgress);
      await updateDoc(doc(db, "users", user.uid), {
        profilePicture: downloadURL,
      });
      setUserData((prevData: any) => ({
        ...prevData,
        profilePicture: downloadURL,
      }));
      await refresh();
      setSaveMessage(t("profilePhotoUpdated"));
      setTimeout(() => setSaveMessage(""), 2500);
    } catch (err: any) {
      console.error("Profile photo upload failed", err);
      if (err instanceof CloudinaryUploadError) {
        if (err.code === "invalid-type") {
          setSaveError(t("invalidImageFile") || err.message);
        } else if (err.code === "too-large") {
          setSaveError(t("imageTooLarge") || err.message);
        } else if (err.code === "unauthenticated") {
          setSaveError(t("uploadPermissionDenied") || err.message);
        } else {
          setSaveError(err.message || t("uploadFailed") || "Upload failed");
        }
      } else if (err?.code === "permission-denied") {
        setSaveError(t("uploadPermissionDenied"));
      } else {
        setSaveError(t("uploadFailed") || "Upload failed");
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    const nextFirst = firstName.trim();
    const nextLast = lastName.trim();
    if (!nextFirst) {
      setSaveError(t("firstNameRequired") || "First name is required");
      return;
    }

    setSaving(true);
    setSaveError("");
    setSaveMessage("");
    try {
      await updateDoc(doc(db, "users", user.uid), {
        firstName: nextFirst,
        lastName: nextLast,
      });
      setUserData((prev: any) => ({
        ...prev,
        firstName: nextFirst,
        lastName: nextLast,
      }));
      await refresh();
      setSaveMessage(t("profileUpdated"));
      setTimeout(() => setSaveMessage(""), 2500);
    } catch (err) {
      console.error(err);
      setSaveError(t("failedToUpdateProfile") || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((n) => n.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2);

  const roleLabel = (() => {
    const role = (userData?.role || "").toLowerCase();
    if (role === "admin") return t("admin");
    if (role === "member") return t("member");
    return userData?.role || "—";
  })();

  const nameDirty =
    firstName.trim() !== (userData?.firstName || "") ||
    lastName.trim() !== (userData?.lastName || "");

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner size="lg" message={t("loading")} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center">
        <p className="text-red-500 dark:text-red-400 font-medium">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
          {t("profile")}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          {t("profileSubtitle")}
        </p>
      </div>

      {(saveMessage || saveError) && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm border ${
            saveError
              ? "border-red-200/70 dark:border-red-800/50 bg-red-50/80 dark:bg-red-900/20 text-red-600 dark:text-red-400"
              : "border-emerald-200/70 dark:border-emerald-800/50 bg-emerald-50/80 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300"
          }`}
        >
          {!saveError && <HiOutlineCheckCircle className="w-5 h-5 shrink-0" />}
          {saveError || saveMessage}
        </div>
      )}

      <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
          <div className="relative shrink-0">
            <div className="w-28 h-28 rounded-2xl overflow-hidden border border-gray-200/80 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              {userData?.profilePicture ? (
                <img
                  src={userData.profilePicture}
                  alt={t("profilePictureAlt")}
                  className="w-full h-full object-cover"
                />
              ) : initials ? (
                <span className="text-3xl font-semibold text-[#5932EA]">
                  {initials}
                </span>
              ) : (
                <HiOutlineUser className="w-12 h-12 text-gray-400" />
              )}
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute -bottom-2 -right-2 inline-flex items-center justify-center w-10 h-10 rounded-xl border border-[#5932EA]/35 bg-[#5932EA]/10 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 disabled:opacity-60 transition-colors shadow-sm"
              title={t("changePhoto")}
              aria-label={t("changePhoto")}
            >
              {uploading ? (
                <span className="w-4 h-4 border-2 border-[#5932EA]/30 border-t-[#5932EA] rounded-full animate-spin" />
              ) : (
                <HiOutlineCamera className="w-5 h-5" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              id="profilePicture"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          <div className="flex-1 min-w-0 text-center sm:text-left space-y-2">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 truncate">
              {[userData?.firstName, userData?.lastName].filter(Boolean).join(" ") ||
                t("profile")}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {userData?.email}
            </p>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 pt-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-[#5932EA]/25 bg-[#5932EA]/10 text-[#4c2bd4] dark:text-[#c4b5fd]">
                <HiOutlineShieldCheck className="w-3.5 h-3.5" />
                {roleLabel}
              </span>
              {userData?.organizationName && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-900/30 text-gray-600 dark:text-gray-300">
                  <HiOutlineBuildingOffice2 className="w-3.5 h-3.5" />
                  {userData.organizationName}
                </span>
              )}
            </div>
            {uploading && (
              <div className="pt-2">
                <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[#5932EA]/70 transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                  {t("uploading")} {uploadProgress}%
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-5 sm:p-6 space-y-4">
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {t("accountDetails")}
        </h3>
        <ul className="space-y-3">
          <li className="flex items-start gap-3 p-3 rounded-xl border border-gray-200/60 dark:border-gray-700/60 bg-white/50 dark:bg-gray-900/25">
            <div className="w-9 h-9 rounded-xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center shrink-0">
              <HiOutlineEnvelope className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">{t("email")}</p>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                {userData?.email || "—"}
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3 p-3 rounded-xl border border-gray-200/60 dark:border-gray-700/60 bg-white/50 dark:bg-gray-900/25">
            <div className="w-9 h-9 rounded-xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center shrink-0">
              <HiOutlineShieldCheck className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">{t("role")}</p>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {roleLabel}
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3 p-3 rounded-xl border border-gray-200/60 dark:border-gray-700/60 bg-white/50 dark:bg-gray-900/25">
            <div className="w-9 h-9 rounded-xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center shrink-0">
              <HiOutlineBuildingOffice2 className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t("organization")}
              </p>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                {userData?.organizationName || "—"}
              </p>
            </div>
          </li>
        </ul>
      </section>

      {isAdmin && (
        <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-5 sm:p-6">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
            {t("homeView") || "Home view"}
          </h3>
          <div className="flex items-start gap-3 p-3.5 rounded-xl border border-gray-200/60 dark:border-gray-700/60 bg-white/50 dark:bg-gray-900/25">
            <div className="w-9 h-9 rounded-xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center shrink-0">
              <HiOutlineHome className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {t("previewMemberHome") || "Preview member home"}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t("previewMemberHomeProfileHint") ||
                  "When on, Home shows the member experience. Sidebar still keeps admin links."}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={previewMemberHome}
              onClick={() => handlePreviewMemberHomeToggle(!previewMemberHome)}
              className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${
                previewMemberHome
                  ? "bg-[#5932EA]"
                  : "bg-gray-300 dark:bg-gray-600"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                  previewMemberHome ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>
        </section>
      )}

      <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-5 sm:p-6">
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
          {t("personalInfo")}
        </h3>
        <form onSubmit={handleSaveName} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                {t("firstName")}
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={inputBase}
                maxLength={80}
                autoComplete="given-name"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                {t("lastName")}
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={inputBase}
                maxLength={80}
                autoComplete="family-name"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving || !nameDirty}
              className={glassBtn}
            >
              {saving ? t("saving") || "Saving…" : t("saveChanges")}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default Profile;
